import { useMemo } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useRelationshipTasks } from 'services/repositories/tasks/tasks';
import { FEATURE_FLAGS } from 'support/helpers/featureFlags/featureFlags';
import { RelationshipV2, TaskDTO } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { ConnectionSetupTableRow } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { StepStatus } from 'components/Display/Steps/Steps';

import { getStepStage, getStepStatus, StepStage } from './helpers';
import { useConfigurationStep } from './useConfigurationStep';
import { useGoLiveStep } from './useGoLiveStep';
import { useTasksStep } from './useTasksStep';
import { useTestingStep } from './useTestingStep';

export type ConnectionSetupStep = {
  stage: StepStage;
  status: StepStatus;
  rows: Array<ConnectionSetupTableRow>;
};

type Params = {
  relationship: RelationshipV2 | undefined;
  partnerId: string | undefined;
  currentPartnerId: string | undefined;
};

export const useSetupConnectionSteps = ({ relationship, partnerId, currentPartnerId }: Params) => {
  const { routeTo } = useConnectionNavigation();
  const checklistEnabled = Boolean(useFeatureFlagEnabled(FEATURE_FLAGS.PORTAL_CHECKLIST_V1));

  const { data: tasksResponse } = useRelationshipTasks({
    variables: { relationshipId: relationship?.id },
    enabled: Boolean(relationship?.id),
  });
  const tasks = tasksResponse?.data ?? [];

  const isSender = relationship?.sender_partner?.id === currentPartnerId;
  const currentStage = relationship ? getStepStage(relationship.status) : 'configuration';

  const activeSections = useActiveSections({ tasks, checklistEnabled, relationship });

  const getStatus = (stage: StepStage) => getStepStatus(stage, currentStage, activeSections);

  const rowsByStage = useStepRows({
    relationship,
    isSender,
    partnerId: partnerId!,
    currentPartnerId,
    routeTo,
    getStatus,
    tasks,
  });

  return useBuildSteps({ relationship, activeSections, currentStage, rowsByStage });
};

const useActiveSections = ({
  tasks,
  checklistEnabled,
  relationship,
}: {
  tasks: Array<TaskDTO>;
  checklistEnabled: boolean;
  relationship: RelationshipV2 | undefined;
}) => {
  const hasTasks = Boolean(tasks.length);
  const requiresTesting = Boolean(relationship?.requires_testing);

  return useMemo<Array<StepStage>>(
    () => [
      ...(hasTasks && checklistEnabled ? (['tasks'] as Array<StepStage>) : []),
      'configuration',
      ...(requiresTesting ? (['testing'] as Array<StepStage>) : []),
      'goLive',
    ],
    [hasTasks, checklistEnabled, requiresTesting],
  );
};

const useStepRows = ({
  relationship,
  isSender,
  partnerId,
  currentPartnerId,
  routeTo,
  getStatus,
  tasks,
}: {
  relationship: RelationshipV2 | undefined;
  isSender: boolean;
  partnerId: string;
  currentPartnerId: string | undefined;
  routeTo: ReturnType<typeof useConnectionNavigation>['routeTo'];
  getStatus: (stage: StepStage) => StepStatus;
  tasks: Array<TaskDTO>;
}) => {
  const { rows: configurationRows } = useConfigurationStep({
    stepStatus: getStatus('configuration'),
  });

  const { rows: testingRows } = useTestingStep({
    stepStatus: getStatus('testing'),
    relationship,
    isSender,
    routeTo,
    partnerId,
  });

  const { rows: goLiveRows } = useGoLiveStep({
    stepStatus: getStatus('goLive'),
    relationship,
    isSender,
    routeTo,
    partnerId,
  });

  const { rows: tasksRows } = useTasksStep({
    stepStatus: getStatus('tasks'),
    tasks,
    currentPartnerId,
    relationship,
    routeTo,
    partnerId,
    relationshipId: relationship?.id,
  });

  return useMemo<Record<string, Array<ConnectionSetupTableRow>>>(
    () => ({
      configuration: configurationRows,
      tasks: tasksRows,
      testing: testingRows,
      goLive: goLiveRows,
    }),
    [configurationRows, tasksRows, testingRows, goLiveRows],
  );
};

const useBuildSteps = ({
  relationship,
  activeSections,
  currentStage,
  rowsByStage,
}: {
  relationship: RelationshipV2 | undefined;
  activeSections: Array<StepStage>;
  currentStage: StepStage;
  rowsByStage: Record<string, Array<ConnectionSetupTableRow>>;
}) => {
  const steps: Array<ConnectionSetupStep> = useMemo(() => {
    if (!relationship) {
      return [];
    }

    return activeSections.map((stage) => ({
      stage,
      status: getStepStatus(stage, currentStage, activeSections),
      rows: rowsByStage[stage] ?? [],
    }));
  }, [relationship, activeSections, currentStage, rowsByStage]);

  const pendingTasksCount = useMemo(
    () => steps.flatMap((step) => step.rows).filter((row) => row.actionRequired).length,
    [steps],
  );

  return { steps, pendingTasksCount };
};
